import { MDXEditorMethods } from '@mdxeditor/editor';
import { Check, Close } from '@mui/icons-material';
import {
  alpha,
  Autocomplete,
  Box,
  Card,
  IconButton,
  Popper,
  Stack,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material';
import { teal as finsightTeal } from '@mui/material/colors';
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { useEditableTextFieldConfiguration } from './EditableTextFieldConfigurationProvider';
import { MDXEditorStyled } from './mdxeditor';

export { EditableTextFieldConfigurationProvider } from './EditableTextFieldConfigurationProvider';

type OptionItem = {
  value: string;
  label?: string;
  description?: string;
};

export default function EditableTextField(
  props: Omit<TextFieldProps, 'onChange' | 'variant'> & {
    onChange?: (v: string) => Promise<void>;
    notBlank?: boolean;
    rich?: boolean;
    numeric?: boolean;
    getLabel?: (v: string) => string;
    variant?: TextFieldProps['variant'] | 'minimal';
    readonly?: boolean;
  } & {
    options?: OptionItem[];
  },
) {
  const {
    value: _value,
    onChange,
    disabled: propsDisabled,
    notBlank,
    readonly,
    variant: _variant,
    numeric,
    rich,
    ...rest
  } = props;

  const configuration = useEditableTextFieldConfiguration();
  const value = _value ?? '';
  const [anchorEl, setAnchorEl] = useState<
    HTMLDivElement | HTMLInputElement | HTMLTextAreaElement | null
  >(null);
  const elem = useRef<HTMLDivElement | null>(null);
  const timeNow = useRef(performance.now());
  const [disabled, setDisabled] = useState(false);
  const [errored, setErrored] = useState(false);
  const [key, setKey] = useState(0);
  const rawValue = useRef<string>(`${value}`);
  const [, refresh] = useReducer((x) => x + 1, 0);

  const optionMap = useMemo(() => {
    const map: Record<string, OptionItem> = {};

    props.options?.forEach((opt) => {
      map[opt.value] = opt;
    });

    return map;
  }, [props.options]);

  const mdxRef = useRef<MDXEditorMethods | null>(null);

  const variant =
    _variant === 'minimal' ? 'standard' : (_variant ?? 'standard');

  useEffect(() => {
    rawValue.current = `${value}`;
    setDisabled(false);
    setErrored(false);
    refresh();
  }, [value]);

  useEffect(() => {
    if (anchorEl) {
      rest.onFocus?.(null as any);
    } else {
      rest.onBlur?.(null as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl]);

  const submitValue = async () => {
    const capturedValue = rawValue.current;
    setAnchorEl(null);
    rawValue.current = `${value}`;
    setDisabled(true);
    try {
      await onChange?.(capturedValue);
    } catch {
      setErrored(true);
    } finally {
      setDisabled(false);
    }
  };

  let invalid = false;

  if (numeric && isNaN(Number(rawValue.current))) invalid = true;
  if (notBlank && !rawValue.current) invalid = true;

  return (
    <>
      <Popper
        open={!!anchorEl}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{
          zIndex: 1400,
        }}
      >
        <Stack gap={0.5} direction="row">
          <Card>
            <IconButton size="small" disabled={invalid} onClick={submitValue}>
              <Check />
            </IconButton>
          </Card>
          <Card>
            <IconButton
              size="small"
              onClick={() => {
                setAnchorEl(null);
                rawValue.current = `${value}`;
                setErrored(false);
                if (rich) {
                  setKey((k) => k + 1);
                }
              }}
            >
              <Close />
            </IconButton>
          </Card>
        </Stack>
      </Popper>
      {rich ? (
        <Box
          ref={elem}
          onClick={() => {
            if (configuration.disabled) return;
            setAnchorEl(elem.current);
            const children = elem.current?.getElementsByClassName(
              '_contentEditable_uazmk_379',
            );

            if (
              children?.length &&
              children[0].tagName === 'DIV' &&
              !children[0].contains(document.activeElement)
            ) {
              children[0].setAttribute('tabindex', '0');
              (children[0] as HTMLDivElement).focus();
            }
          }}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gridTemplateRows: '1fr',
            '& > *': {
              gridColumn: 1,
              gridRow: 1,
            },
            ...(disabled || configuration.disabled
              ? {
                  pointerEvents: 'none',
                  opacity: 0.7,
                }
              : {}),
          }}
        >
          <MDXEditorStyled
            key={key}
            ref={mdxRef}
            markdown={rawValue.current}
            originalValue={`${value}`}
            readOnly={!anchorEl}
            trim={false}
            onChange={(v) => (rawValue.current = v)}
          />
          {props.placeholder && !anchorEl && !rawValue.current.length && (
            <Box
              sx={{
                mt: 1,
                padding: 2,
                pointerEvents: 'none',
              }}
            >
              {props.placeholder}
            </Box>
          )}
        </Box>
      ) : (
        <Tooltip
          title={
            readonly ? 'Readonly' : configuration.disabled ? 'Disabled' : ''
          }
        >
          <Autocomplete
            key={key}
            freeSolo
            disableClearable
            options={rest.options?.map((x) => x.value) ?? []}
            getOptionLabel={(option) => optionMap[option]?.label ?? option}
            fullWidth
            disabled={disabled || propsDisabled || configuration.disabled}
            onChange={async (_, v) => {
              if (anchorEl) {
                rawValue.current = v;
                submitValue();
              }
            }}
            onInputChange={(_, v) => {
              if (configuration.requireConfirmation === false) {
                onChange?.(v);
                return;
              }
              if (anchorEl) {
                rawValue.current = v;
                refresh();
              }
            }}
            onFocus={(e) => {
              if (readonly || configuration.disabled) return;
              timeNow.current = performance.now();
              setAnchorEl(e.currentTarget);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                rawValue.current = `${value}`;
                setAnchorEl(null);
                setKey((k) => k + 1);
              }
            }}
            onBlur={() => {
              if (
                rawValue.current === `${value}` &&
                performance.now() - timeNow.current > 100
              ) {
                setAnchorEl(null);
                // setKey((k) => k + 1);
                elem.current?.blur();
              }
            }}
            value={
              anchorEl
                ? rawValue.current
                : props.getLabel
                  ? props.getLabel(rawValue.current)
                  : rawValue.current
            }
            renderInput={(params) => (
              <TextField
                {...rest}
                {...params}
                ref={elem}
                variant={variant}
                multiline
                error={errored || invalid}
                helperText={
                  disabled ? 'Saving...' : errored ? 'Error' : rest.helperText
                }
                InputProps={{
                  ...rest.InputProps,
                  ...params.InputProps,
                  readOnly: readonly || configuration.disabled,
                }}
                sx={{
                  ...rest.sx,
                  minWidth: 200,
                }}
              />
            )}
            sx={{
              ...props.sx,
              ...(_variant === 'minimal'
                ? {
                    '.MuiInputBase-root::before': {
                      border: 'none',
                    },
                  }
                : {}),
            }}
            slotProps={{
              popper: {
                sx: {
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: alpha(finsightTeal[800], 0.1),
                    borderRadius: '0.5em',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: alpha(finsightTeal[700], 0.5),
                    borderRadius: '0.5em',
                  },
                  '& .MuiAutocomplete-listbox': {
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: alpha(finsightTeal[800], 0.1),
                      borderRadius: '0.5em',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: alpha(finsightTeal[700], 0.5),
                      borderRadius: '0.5em',
                    },
                  },
                },
              },
            }}
          />
        </Tooltip>
      )}
    </>
  );
}
